<template>
  <div class="checkout">
    <div class="checkout__header" v-if="businessLogo && businessName">
      <img :src="businessLogo" alt="logo" />
      <h4>{{ businessName }}</h4>
    </div>
    <form @submit.prevent.stop="getCardToken" id="paymentForm">
      <div class="w-section">
        <h4 class="w-section__title">Detalles del comprador</h4>
        <div class="section">
          <div class="field" :class="{ error: errors['email'] }">
            <label for="email">{{mapLabels.email}}</label>
            <input
              class="input"
              id="email"
              name="email"
              type="text"
              v-model="form.email"
              @input="onValidate('email')"
            />
          </div>
          <div
            class="field field-doctype"
            :class="{ error: errors['docType'] }"
          >
            <label for="docType">{{mapLabels.docType}}</label>
            <select
              id="docType"
              v-model="form.docType"
              name="docType"
              data-checkout="docType"
              type="text"
              @input="onValidate('docType')"
            >
              <option v-for="doc of docTypes" :key="doc.id" :value="doc.id">
                {{ doc.id }}
              </option>
            </select>
          </div>
          <div
            class="field field-docnumber"
            :class="{ error: errors['docNumber'] }"
          >
            <label for="docNumber">{{mapLabels.docNumber}}</label>
            <input
              class="input"
              id="docNumber"
              name="docNumber"
              v-model="form.docNumber"
              data-checkout="docNumber"
              type="text"
              @input="onValidate('docNumber')"
            />
          </div>
        </div>
      </div>
      <div class="w-section">
        <h4 class="w-section__title">Detalles de la tarjeta</h4>
        <div class="section">
          <div class="field" :class="{ error: errors['cardholderName'] }">
            <label for="cardholderName">{{mapLabels.cardHolderName}}</label>
            <input
              class="input"
              id="cardholderName"
              v-model="form.cardHolderName"
              data-checkout="cardholderName"
              type="text"
              @input="onValidate('cardholderName')"
            />
          </div>
          <div class="field" :class="{ error: errors['cardNumber'] }">
            <label for="cardNumber">{{mapLabels.cardNumber}}</label>
            <input
              class="input"
              type="text"
              id="cardNumber"
              data-checkout="cardNumber"
              onselectstart="return false"
              on-copy="return false"
              on-paste="return false"
              oncut="return false"
              ondrag="return false"
              ondrop="return false"
              autocomplete="off"
              v-model="form.cardNumberMask"
              v-mask="'#### #### #### ####'"
              @change="guessPaymentMethod"
              @input="onValidate('cardNumberMask')"
            />
            <img class="card-logo" :src="cardLogo" alt="" v-if="cardLogo" />
          </div>
          <div class="field field-50 field-dates">
            <label for="">{{mapLabels.cardExpiration}}</label>
            <div>
              <span :class="{ error: errors['cardExpirationMonth'] }">
                <input
                  type="text"
                  placeholder="MM"
                  id="cardExpirationMonth"
                  data-checkout="cardExpirationMonth"
                  onselectstart="return false"
                  onpaste="return false"
                  oncopy="return false"
                  oncut="return false"
                  ondrag="return false"
                  ondrop="return false"
                  autocomplete="off"
                  v-model.number="form.cardExpirationMonth"
                  size="2"
                  minlength="2"
                  maxlength="2"
                  @input="onValidate('cardExpirationMonth')"
                />
              </span>
              <span class="date-separator">/</span>
              <span :class="{ error: errors['cardExpirationYear'] }">
                <input
                  type="text"
                  placeholder="YY"
                  id="cardExpirationYear"
                  data-checkout="cardExpirationYear"
                  onselectstart="return false"
                  onpaste="return false"
                  oncopy="return false"
                  oncut="return false"
                  ondrag="return false"
                  ondrop="return false"
                  autocomplete="off"
                  v-model.number="form.cardExpirationYear"
                  size="2"
                  minlength="2"
                  maxlength="2"
                  @input="onValidate('cardExpirationYear')"
                />
              </span>
            </div>
          </div>

          <div
            class="field field-50 field-ccv"
            :class="{ error: errors['securityCode'] }"
          >
            <label for="securityCode">{{mapLabels.securityCode}}</label>
            <input
              id="securityCode"
              data-checkout="securityCode"
              type="text"
              onselectstart="return false"
              onpaste="return false"
              oncopy="return false"
              oncut="return false"
              ondrag="return false"
              ondrop="return false"
              autocomplete="off"
              maxlength="5"
              minlength="3"
              size="5"
              v-model="form.securityCode"
              @input="onValidate('securityCode')"
            />
          </div>
          <div
            class="field"
            :class="{ error: errors['issuer'] }"
            id="issuerInput"
            v-if="needIssuer"
          >
            <label for="issuer">Banco emisor</label>
            <select
              v-model="form.issuer"
              id="issuer"
              name="issuer"
              data-checkout="issuer"
              @change="onValidate('issuer')"
            ></select>
          </div>
          <div
            v-show="needInstallments"
            :class="{ error: errors['installments'] }"
            class="field"
          >
            <label for="installments">Cuotas</label>
            <select
              type="text"
              v-model="form.installments"
              id="installments"
              name="installments"
              @change="onValidate('installments')"
            ></select>
          </div>
          <div class="field">
            <input
              type="hidden"
              name="transactionAmount"
              id="transactionAmount"
              v-model="form.transactionAmount"
              @input="onValidate('transactionAmount')"
            />
            <input
              type="hidden"
              v-model="form.paymentMethodId"
              name="paymentMethodId"
              id="paymentMethodId"
              @input="onValidate('paymentMethodId')"
            />
            <input
              type="hidden"
              v-model="form.description"
              name="description"
              id="description"
              @input="onValidate('description')"
            />
            <div v-if="firstError" class="danger-alert">
              {{ firstError.message }}
            </div>
            <br />
            <div v-if="!loader">
              <button
                type="submit"
                :class="{ 'active-pay': isValid }"
                :disabled="!isValid"
              >
                Pagar
              </button>
            </div>
            <div v-else-if="loader">
              <button :class="{ 'active-pay': isValid }">
                <span class="spiner"></span>
              </button>
            </div>
            <br />
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { VueMaskDirective } from "v-mask";
export default {
  name: "checkout-mp",
  inject: ["publicKey", "business", "buyer", "purchase","singleQuote","mode","formLabels"],
  directives: {
    mask: VueMaskDirective,
  },
  props: {
    onRequestPayment: {},
  },
  data() {
    return {
      businessName: null,
      businessLogo: null,
      loader: false,
      docTypes: [],
      mapLabels:{
        email:"E-mail",
        docType:"Tipo de documento",
        docNumber:'Número de documento',
        cardHolderName:'Titular de la tarjeta',
        cardNumber:'Número de la tarjeta',
        cardExpiration:"Fecha de vencimiento",
        securityCode:"Código de seguridad"
      },
      form: {
        email: null,
        docType: null,
        docNumber: null,
        cardNumberMask: null,
        cardHolderName: null,
        cardExpirationMonth: null,
        cardExpirationYear: null,
        securityCode: null,
        issuer: null,
        installments: 1,
        transactionAmount: null,
        paymentMethodId: null,
        description: null,
      },
      validationMap: {
        email: "required",
        docType: "required",
        docNumber: "required",
        cardHolderName: "required",
        cardNumberMask: "required",
        cardExpirationMonth: "required",
        cardExpirationYear: "required",
        securityCode: "required",
      },
      firstError: null,
      errors: {},
      doSubmit: false,
      needIssuer: false,
      needInstallments: false,
      token: null,
      preorderId: null,
      cardLogo: null,
      isPaymentDisabled: true,
      isValid: false,
    };
  },
  beforeCreate() {},
  created() {
    if(this.mode=='resume'){
      Object.assign(this.mapLabels,{
        email:'Email',
        docType:"Tipo de Doc.",
        docNumber:'Nº de Documento',
        cardHolderName:'Titular de la tarjeta',
        cardNumber:'Nº de la tarjeta',
        cardExpiration:"F. de vencimiento",
        securityCode:"CVV"
      })
    }
    this.mapLabels=Object.assign(this.mapLabels,this.formLabels)
    const promise = () => {
      return new Promise((resolve) => {
        window.Mercadopago.setPublishableKey(this.publicKey);
        window.Mercadopago.getIdentificationTypes((status, list) => {
          this.docTypes = list;
          resolve(true);
        });
      });
    };
    promise().then(() => {
      this.form.docType = this.buyer.documentType;
      //INICIA
      this.businessName = this.business.name;
      this.businessLogo = this.business.logo;

      //datos de comprador
      this.form.email = this.buyer.email;
      this.form.docNumber = this.buyer.documentNumber;
      this.form.cardHolderName = this.buyer.name;

      this.form.transactionAmount = this.purchase.amount;
      this.form.description = this.purchase.description;
      this.form.preorderId = this.purchase.preorderId;
    });
  },
  computed: {
    cardNumber() {
      if (!this.form.cardNumberMask) return "";
      return this.form.cardNumberMask.split(" ").join("");
    },
  },
  methods: {
    errorsHandler(response) {
      var errors = {};
      this.firstError = null;
      if (response.cause && response.cause.length) {
        var tempError = response.cause[0];
        this.firstError = {
          error: true,
          message: null,
        };
        switch (tempError.code) {
          case "205":
            this.firstError.message = "Ingresa el número de tu tarjeta";
            errors["cardNumberMask"] = this.firstError;
            break;
          case "208":
            this.firstError.message = "Elige un mes";
            errors["cardExpirationMonth"] = this.firstError;
            break;
          case "209":
            this.firstError.message = "Elige un año";
            errors["cardExpirationYear"] = this.firstError;
            break;
          case "212":
            this.firstError.message = "Ingresa tu tipo de documento";
            errors["docType"] = this.firstError;
            break;
          case "213":
            this.firstError.message = "Ingresa tu documento";
            errors["docNumber"] = this.firstError;
            break;
          case "214":
            this.firstError.message = "Ingresa tu documento";
            errors["docNumber"] = this.firstError;
            break;
          case "220":
            this.firstError.message = "Ingresa tu banco";
            errors["issuer"] = this.firstError;
            break;
          case "221":
            this.firstError.message = "Ingresa el nombre y apellido";
            errors["cardholderName"] = this.firstError;
            break;
          case "224":
            this.firstError.message = "Ingresa el código de seguridad";
            errors["securityCode"] = this.firstError;
            break;
          case "E301":
            this.firstError.message = "Ingresa un número de tarjeta válido";
            errors["cardNumberMask"] = this.firstError;
            break;
          case "E302":
            this.firstError.message = "Revisa el código de seguridad";
            errors["securityCode"] = this.firstError;
            break;
          case "316":
            this.firstError.message = "Ingresa un nombre válido";
            errors["cardHolderName"] = this.firstError;
            break;
          case "322":
            this.firstError.message = "El tipo de documento es inválido";
            errors["docType"] = this.firstError;
            break;
          case "323":
            this.firstError.message = "Revisa tu documento";
            errors["docNumber"] = this.firstError;
            break;
          case "324":
            this.firstError.message = "El documento es inválido";
            errors["docNumber"] = this.firstError;
            break;
          case "325":
            this.firstError.message = "El mes es inválido";
            errors["cardExpirationMonth"] = this.firstError;
            break;
          case "326":
            this.firstError.message = "El año es inválido";
            errors["cardExpirationYear"] = this.firstError;
            break;
          //ERRORE DE GENERACION DE TOKEN
          case "106":
            this.firstError.message = "No puedes realizar pagos a otros países";
            break;
          case "109":
            this.firstError.message = `${this.form.paymentMethodId} no procesa pagos en ${this.form.installments} cuotas`;
            break;
          case "126":
            this.firstError.message = "No pudimos procesar tu pago";
            break;
          case "129":
            this.firstError.message = `${this.form.paymentMethodId} no procesa pagos del monto seleccionado, elige otra tarjeta u otro medio de pago.`;
            break;
          case "145":
            this.firstError.message = `Una de las partes con la que intentas hacer el pago es de prueba y la otra es usuario real`;
            break;
          case "150":
            this.firstError.message = `No puedes realizar pagos`;
            break;
          case "151":
            this.firstError.message = `No puedes realizar pagos`;
            break;
          case "160":
            this.firstError.message = `No pudimos procesar tu pago`;
            break;
          case "204":
            this.firstError.message = `${this.form.paymentMethodId} no está disponible en este momento`;
            break;
          case "801":
            this.firstError.message = `Realizaste un pago similar hace instantes. Intenta de nuevo en unos minutos`;
            break;
          default:
            this.firstError.message =
              "Verifique sus datos, no pudimos procesar tu pago";
            break;
        }
      } else {
        this.firstError = {
          error: true,
          message: "Algo anda mal, verifique sus datos",
        };
      }
      Object.assign(this.errors, errors);
    },
    onValidate(name) {
      var errorsReq = {};
      var isValid = true;
      for (var val in this.validationMap) {
        errorsReq[val] = {
          validation: this.validationMap[val],
          error: true,
        };
        if (this.form[val]) errorsReq[val] = false;
        else isValid = false;
      }
      if (this.form[name]) {
        errorsReq[name] = false;
      }
      this.isValid = isValid;
    },
    guessPaymentMethod() {
      let cardnumber = this.cardNumber;
      if (cardnumber.length >= 6) {
        let bin = cardnumber.substring(0, 6);
        window.Mercadopago.getPaymentMethod(
          {
            bin: bin,
          },
          this.setPaymentMethod
        );
      }
    },
    setPaymentMethod(status, response) {
      if (status == 200) {
        let paymentMethod = response[0];
        this.form.paymentMethodId = paymentMethod.id;
        this.cardLogo = paymentMethod.secure_thumbnail;
        if (paymentMethod.additional_info_needed.includes("issuer_id")) {
          this.needIssuer = true;
          this.getIssuers(paymentMethod.id);
        } else {
          this.needIssuer = false;
          this.getInstallments(paymentMethod.id, this.transactionAmount);
        }
      } else {
        this.errorsHandler(response);
      }
    },
    getIssuers(paymentMethodId) {
      window.Mercadopago.getIssuers(paymentMethodId, this.setIssuers);
    },
    setIssuers(status, response) {
      if (status == 200) {
        let issuerSelect = document.getElementById("issuer");
        response.forEach((issuer) => {
          let opt = document.createElement("option");
          opt.text = issuer.name;
          opt.value = issuer.id;
          issuerSelect.appendChild(opt);
        });

        this.getInstallments(
          this.paymentMethodId,
          this.transactionAmount,
          issuerSelect.value
        );
      } else {
        this.errorsHandler(response);
      }
    },
    getInstallments(paymentMethodId, transactionAmount, issuerId) {
      window.Mercadopago.getInstallments(
        {
          payment_method_id: paymentMethodId,
          amount: parseFloat(transactionAmount),
          issuer_id: issuerId ? parseInt(issuerId) : undefined,
        },
        this.setInstallments
      );
    },
    setInstallments(status, response) {
      // console.log(response);
      if (status == 200) {
        this.needInstallments = true;
        document.getElementById("installments").options.length = 0;
        for (var payerCost of response[0].payer_costs) {
          if(this.singleQuote&&payerCost.installments==1){
            this.form.installments=1;
            break;
          }
          let opt = document.createElement("option");
          opt.text = payerCost.recommended_message;
          opt.value = payerCost.installments;
          document.getElementById("installments").appendChild(opt);
        }
        if (document.getElementById("installments").options.length > 0) {
          this.needInstallments = true;
        } else {
          this.needInstallments = false;
        }
      } else {
        this.errorsHandler(response);
      }
    },
    getCardToken() {
      if (!this.doSubmit) {
        this.timeout = setTimeout(() => {
          this.errorsHandler({});
        }, 5000);
        let $form = document.getElementById("paymentForm");
        window.Mercadopago.createToken($form, this.setCardTokenAndPay);
        return false;
      }
    },
    handler() {
      var ctx = this;
      return {
        init() {
          ctx.loader = true;
        },
        finish() {
          ctx.loader = false;
        },
      };
    },
    setCardTokenAndPay(status, response) {
      clearTimeout(this.timeout);
      if (status == 200 || status == 201) {
        let form = document.getElementById("paymentForm");
        let card = document.createElement("input");
        card.setAttribute("name", "token");
        card.setAttribute("type", "hidden");
        card.setAttribute("value", response.id);
        form.appendChild(card);
        this.token = response.id;
        //payment object
        var payment = {
          transactionAmount: this.form.transactionAmount,
          description: this.form.description,
          email: this.form.email,
          cardHolderName: this.form.cardHolderName,
          token: this.token,
          installments: this.form.installments
            ? this.form.installments
            : undefined,
          issuer: this.form.issuer ? this.form.issuer : undefined,
          paymentMethodId: this.form.paymentMethodId,
          docType: this.form.docType,
          docNumber: this.form.docNumber,
          preorder: this.form.preorderId,
        };
        this.errors = {};
        if (this.onRequestPayment) {
          this.onRequestPayment(payment, this.handler());
          this.doSubmit = true;
        } else {
          this.$root.$emit("onRequestPayment", payment, this.handler());
          this.doSubmit = true;
        }
      } else {
        //MANEJO DE ERRORES
        this.errorsHandler(response);
        this.doSubmit = false;
        // alert(response)
      }
    },
  },
};
</script>

<style lang="scss" scoped>
input,
select {
  border: solid 1px grey;
}

.checkout {
  --p_bg-color: #2c3e50;
  --t_color: #d63c3c;

  min-width: 320px;
  max-width: 450px;
  width: 100%;
  margin: 0 auto;
  border: thin solid #cdcdcd;

  .danger-alert {
    color: var(--t_color);
  }

  .w-section {
    .error {
      input {
        border: 1px solid var(--t_color);
      }
    }
    &__title {
      font-size: 1.1rem;
    }
  }
  &__header {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
    padding: 1rem;
    background-color: #ededed;
    img {
      flex: 0 0 40px;
      width: 40px;
      height: 40px;
      padding: 0.5rem;
      background-color: #fff;
      border: thin solid #cdcdcd;
      border-radius: 5px;
    }
    h4 {
      font-size: 2rem;
      margin: 0 1rem;
    }
  }
  .section {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
    align-items: stretch;
    padding: 8px;
  }
  .field {
    width: 100%;
    box-sizing: border-box;
    margin: 0.5rem 0;
    position: relative;
    &-50 {
      width: 50%;
    }
    label {
      width: 100%;
      display: block;
      font-size: 16px;
      text-align: start;
      margin: 4px 0;
    }
    .input,
    select {
      width: 100%;
      font-size: 16px;
      box-sizing: border-box;
      margin: 0px;
    }
    input,
    select {
      height: 30px;
      box-sizing: border-box;
    }
    button {
      width: 100%;
      height: 40px;
      text-align: center;
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 1rem;
      cursor: pointer;
      justify-content: center;
      display: flex;
      align-items: center;
      color: #999;
      border: none;
      border-radius: 15px;
    }
    .card-logo {
      height: 10px;
      position: absolute;
      bottom: 10px;
      right: 10px;
    }
  }
  .field-doctype {
    width: 40%;
  }
  .field-docnumber {
    width: 50%;
  }
  .field-dates,
  .field-ccv {
    text-align: left;
    input {
      padding: 0px 15px;
      width: 60px;
    }
  }
  .field-ccv {
    input {
      text-align: center;
      min-width: 150px;
    }
  }
  .active-pay {
    color: white !important;
    background: var(--p_bg-color);
  }
}
.spiner {
  --s-loader-width: 30px;
  --s-loader-height: 30px;
  --s-loader-border-color-trans: rgba(29, 161, 242, 0.2);
  --s-loader-border-color-full: rgb(29, 161, 242);

  width: var(--s-loader-width);
  height: var(--s-loader-height);
  border: 5px solid var(--s-loader-border-color-trans);
  border-left-color: var(--s-loader-border-color-full);
  border-radius: 50%;
  background: transparent;
  animation-name: rotate-s-loader;
  animation-iteration-count: infinite;
  animation-duration: 1s;
  animation-timing-function: linear;
  position: relative;
}
@keyframes rotate-s-loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
